/**
 * Transaction History Page
 * Detailed transaction history with advanced filtering and analytics
 */

import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import {
  History,
  Download,
  Filter,
  Search,
  Calendar,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  Eye,
  MoreHorizontal
} from 'lucide-react';

interface Transaction {
  id: string;
  customer_id: string;
  customer_name: string;
  customer_email: string;
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'cancelled';
  payment_method: 'card' | 'bank' | 'wallet' | 'crypto';
  transaction_id: string;
  gateway_transaction_id: string;
  created_at: string;
  updated_at: string;
  description: string;
  notes?: string;
  metadata?: {
    ip_address?: string;
    user_agent?: string;
    billing_address?: any;
    shipping_address?: any;
  };
  fees?: {
    gateway_fee: number;
    processing_fee: number;
    total_fees: number;
  };
}

export default function TransactionHistoryPage() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [methodFilter, setMethodFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('30');
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  useEffect(() => {
    loadTransactions();
  }, [statusFilter, methodFilter, dateRange]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      // Mock comprehensive transaction data
      const mockTransactions: Transaction[] = [
        {
          id: '1',
          customer_id: 'cust_001',
          customer_name: 'John Smith',
          customer_email: 'john.smith@email.com',
          amount: 29900,
          currency: 'USD',
          status: 'completed',
          payment_method: 'card',
          transaction_id: 'stx_1234567890',
          gateway_transaction_id: 'gw_9876543210',
          created_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
          updated_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
          description: 'Monthly warranty payment',
          notes: 'Auto-renewal payment',
          metadata: {
            ip_address: '192.168.1.100',
            user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
            billing_address: {
              street: '123 Main St',
              city: 'Anytown',
              state: 'CA',
              zip: '12345'
            }
          },
          fees: {
            gateway_fee: 89,
            processing_fee: 30,
            total_fees: 119
          }
        },
        {
          id: '2',
          customer_id: 'cust_002',
          customer_name: 'Sarah Johnson',
          customer_email: 'sarah.j@email.com',
          amount: 19900,
          currency: 'USD',
          status: 'pending',
          payment_method: 'bank',
          transaction_id: 'stx_1234567891',
          gateway_transaction_id: 'gw_9876543211',
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
          updated_at: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
          description: 'Setup fee',
          metadata: {
            ip_address: '10.0.0.50',
            billing_address: {
              street: '456 Oak Ave',
              city: 'Somewhere',
              state: 'NY',
              zip: '67890'
            }
          },
          fees: {
            gateway_fee: 60,
            processing_fee: 20,
            total_fees: 80
          }
        },
        {
          id: '3',
          customer_id: 'cust_003',
          customer_name: 'Mike Davis',
          customer_email: 'mike.davis@email.com',
          amount: 45000,
          currency: 'USD',
          status: 'failed',
          payment_method: 'card',
          transaction_id: 'stx_1234567892',
          gateway_transaction_id: 'gw_9876543212',
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
          updated_at: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
          description: 'Annual premium plan',
          notes: 'Card expired - customer notified',
          metadata: {
            ip_address: '172.16.0.25',
            user_agent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X)',
            billing_address: {
              street: '789 Pine St',
              city: 'Elsewhere',
              state: 'TX',
              zip: '11111'
            }
          },
          fees: {
            gateway_fee: 135,
            processing_fee: 45,
            total_fees: 180
          }
        },
        {
          id: '4',
          customer_id: 'cust_004',
          customer_name: 'Emily Chen',
          customer_email: 'emily.chen@email.com',
          amount: 75000,
          currency: 'USD',
          status: 'refunded',
          payment_method: 'wallet',
          transaction_id: 'stx_1234567893',
          gateway_transaction_id: 'gw_9876543213',
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
          updated_at: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
          description: 'Premium service package',
          notes: 'Refunded per customer request - service cancellation',
          metadata: {
            ip_address: '203.0.113.10',
            billing_address: {
              street: '321 Elm St',
              city: 'Another Town',
              state: 'FL',
              zip: '22222'
            }
          },
          fees: {
            gateway_fee: 225,
            processing_fee: 75,
            total_fees: 300
          }
        }
      ];

      let filtered = mockTransactions;

      // Apply filters
      if (statusFilter !== 'all') {
        filtered = filtered.filter(t => t.status === statusFilter);
      }
      if (methodFilter !== 'all') {
        filtered = filtered.filter(t => t.payment_method === methodFilter);
      }
      if (searchTerm) {
        filtered = filtered.filter(t =>
          t.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.customer_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.transaction_id.includes(searchTerm)
        );
      }

      setTransactions(filtered);
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
    setLoading(false);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: 'bg-green-500/10 text-green-400 border-green-500/20',
      pending: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      failed: 'bg-red-500/10 text-red-400 border-red-500/20',
      refunded: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      cancelled: 'bg-gray-500/10 text-gray-400 border-gray-500/20'
    };

    return variants[status as keyof typeof variants] || variants.pending;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle size={16} className="text-green-400" />;
      case 'pending': return <Clock size={16} className="text-yellow-400" />;
      case 'failed': return <AlertTriangle size={16} className="text-red-400" />;
      case 'refunded': return <RefreshCw size={16} className="text-blue-400" />;
      case 'cancelled': return <AlertTriangle size={16} className="text-gray-400" />;
      default: return <Clock size={16} className="text-gray-400" />;
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalVolume = () => {
    return transactions
      .filter(t => t.status === 'completed')
      .reduce((sum, t) => sum + t.amount, 0);
  };

  const getSuccessRate = () => {
    const completed = transactions.filter(t => t.status === 'completed').length;
    const total = transactions.length;
    return total > 0 ? ((completed / total) * 100).toFixed(1) : '0.0';
  };

  return (
    <AdminPageLayout
      title="Transaction History"
      subtitle="Complete transaction history with detailed analytics"
      actions={
        <div className="flex gap-2">
          <Button variant="outline">
            <Download size={16} className="mr-2" />
            Export CSV
          </Button>
          <Button variant="outline">
            <Calendar size={16} className="mr-2" />
            Date Range
          </Button>
        </div>
      }
    >
      {/* Transaction Statistics */}
      <StatsGrid cols={4}>
        <StatCard
          label="Total Volume"
          value={formatAmount(getTotalVolume())}
          icon={DollarSign}
          subtitle={`${transactions.filter(t => t.status === 'completed').length} successful transactions`}
        />
        <StatCard
          label="Success Rate"
          value={`${getSuccessRate()}%`}
          icon={TrendingUp}
          subtitle={`From ${transactions.length} total transactions`}
        />
        <StatCard
          label="Pending Review"
          value={transactions.filter(t => t.status === 'pending').length.toString()}
          icon={Clock}
          subtitle="Require attention"
        />
        <StatCard
          label="Failed Transactions"
          value={transactions.filter(t => t.status === 'failed').length.toString()}
          icon={AlertTriangle}
          subtitle="Need follow-up"
        />
      </StatsGrid>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="grid grid-cols-4 gap-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search transactions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={(value: string) => setStatusFilter(value)}>
            <SelectTrigger>
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={methodFilter} onValueChange={(value: string) => setMethodFilter(value)}>
            <SelectTrigger>
              <SelectValue placeholder="All Methods" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Methods</SelectItem>
              <SelectItem value="card">Credit Card</SelectItem>
              <SelectItem value="bank">Bank Account</SelectItem>
              <SelectItem value="wallet">Digital Wallet</SelectItem>
              <SelectItem value="crypto">Cryptocurrency</SelectItem>
            </SelectContent>
          </Select>
          <Select value={dateRange} onValueChange={(value: string) => setDateRange(value)}>
            <SelectTrigger>
              <SelectValue placeholder="Date Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Transactions Table */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Transaction Details</h3>
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-slate-800/50 animate-pulse rounded"></div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="border border-slate-700 rounded-lg p-4 hover:bg-slate-800/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {getStatusIcon(transaction.status)}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-medium text-white">{transaction.customer_name}</span>
                        <span className="text-sm text-slate-400">{transaction.customer_email}</span>
                        <Badge className={getStatusBadge(transaction.status)}>
                          {transaction.status.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="text-sm text-slate-400 mb-1">
                        {transaction.description} • {transaction.transaction_id}
                      </div>
                      <div className="text-xs text-slate-500">
                        {formatDate(transaction.created_at)} • {transaction.payment_method.toUpperCase()}
                        {transaction.fees && (
                          <span className="ml-3">
                            Fees: {formatAmount(transaction.fees.total_fees)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-white mb-1">
                      {formatAmount(transaction.amount)}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedTransaction(transaction)}
                      >
                        <Eye size={14} className="mr-1" />
                        Details
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <Card className="p-6 mt-6 border-blue-500/20 bg-blue-500/5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Transaction Details</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedTransaction(null)}
            >
              Close
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-2">Transaction Information</h4>
              <div className="space-y-2 text-sm">
                <div><strong>Transaction ID:</strong> {selectedTransaction.transaction_id}</div>
                <div><strong>Gateway ID:</strong> {selectedTransaction.gateway_transaction_id}</div>
                <div><strong>Status:</strong> <Badge className={getStatusBadge(selectedTransaction.status)}>{selectedTransaction.status.toUpperCase()}</Badge></div>
                <div><strong>Amount:</strong> {formatAmount(selectedTransaction.amount)}</div>
                <div><strong>Method:</strong> {selectedTransaction.payment_method.toUpperCase()}</div>
                <div><strong>Created:</strong> {formatDate(selectedTransaction.created_at)}</div>
                <div><strong>Updated:</strong> {formatDate(selectedTransaction.updated_at)}</div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">Customer Information</h4>
              <div className="space-y-2 text-sm">
                <div><strong>Name:</strong> {selectedTransaction.customer_name}</div>
                <div><strong>Email:</strong> {selectedTransaction.customer_email}</div>
                <div><strong>Customer ID:</strong> {selectedTransaction.customer_id}</div>
                {selectedTransaction.metadata?.ip_address && (
                  <div><strong>IP Address:</strong> {selectedTransaction.metadata.ip_address}</div>
                )}
              </div>
              {selectedTransaction.fees && (
                <>
                  <h4 className="font-medium mb-2 mt-4">Fee Breakdown</h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Gateway Fee:</strong> {formatAmount(selectedTransaction.fees.gateway_fee)}</div>
                    <div><strong>Processing Fee:</strong> {formatAmount(selectedTransaction.fees.processing_fee)}</div>
                    <div><strong>Total Fees:</strong> {formatAmount(selectedTransaction.fees.total_fees)}</div>
                    <div><strong>Net Amount:</strong> {formatAmount(selectedTransaction.amount - selectedTransaction.fees.total_fees)}</div>
                  </div>
                </>
              )}
            </div>
          </div>
          {selectedTransaction.notes && (
            <div className="mt-4">
              <h4 className="font-medium mb-2">Notes</h4>
              <p className="text-sm text-slate-300 bg-slate-800/50 p-3 rounded">
                {selectedTransaction.notes}
              </p>
            </div>
          )}
        </Card>
      )}
    </AdminPageLayout>
  );
}
